import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MarsAdmin from '../MarsAdmin';
import MarsFamilyComplete from '../MarsFamilyComplete'; // Ensure this component is imported
import RequesterApp from '../MarsRequestor';
import { loginRequest, resetPassword } from '../authentification/msalConfig.js';
import Logo from '../logo.png';
import { POST_LOGIN, 
        GET_SITETYPES, 
        COUNTRY_URL, 
        EVENT_TYPE_URL, 
        EVENT_FORMAT_URL,
        EVENT_LAST_VISIT_URL,
        EVENT_ASSOCIATE_URL,
        EVENT_STATUS_URL,
        GET_SITECODES,
        SITE_DETAILS_URL,
        GET_REGIONS,
        COUNTRY_CODE_URL,
        STATE_URL,
        CITY_URL,
        USER_TYPES_URL,
    } from './ApiDetails.js';
import InformationPopup from './InformationPopup';
import './LoginForm.css';

const Login = () => {

    const { instance } = useMsal();
    const navigate = useNavigate();

    const [loginResponse, setLoginResponse] = useState({ message: '', hasError: false });
    const [showApp, setShowApp] = useState('');  // Store which app to show (Requester, Admin, Family)
    const [showPopup, setShowPopup] = useState(false);  // Control for showing popup
    const [loading, setLoading] = useState(false);
    const [loginInProgress, setLoginInProgress] = useState(false);

    const setAppToShow = (role) => {
        switch (role) {
            case 'Visit Requester':
                setShowApp('Requester');  // Show RequesterApp in background
                break;
            case 'Admin':
                setShowApp('Admin');  // Show Admin in background
                break;
            case 'Family':
                setShowApp('Family');  // Show Family app in background
                break;
            case 'MLTUser':
                setShowApp('MLTUser');
                break;
            case 'FamilyCoordinator':
                setShowApp('FamilyCoordinator');
                break;
            case 'MLTCoordinator':
                setShowApp('MLTCoordinator');
                break;
            default:
                setShowApp('');
                break;
        }
    }

    const navigateToApp = (role) => {
        // Role-based redirection
        switch (role) {
            case 'Visit Requester':
            case 'Requester':
                navigate('/AppRequester');
                break;
            case 'Admin':
                navigate('/AppAdmin');
                break;
            case 'Family':
                navigate('/AppFamily');
                break;
            case 'MLTUser':
                navigate('/AppMltEvent');
                break;
            case 'FamilyCoordinator':
                navigate('/AppFamily');
                break;
            case 'MLTCoordinator':
                navigate('/AppMltEvent');
                break;
            default:
                navigate('/');  // Redirect to home or login in case of an invalid role
                break;
        }
    }

    // Check if popup is hidden, and automatically navigate if so
    useEffect(() => {
        const storedRole = localStorage.getItem('role');
        const popupSkipped = localStorage.getItem('hidePopup') === 'true';
        if (popupSkipped && storedRole) {
            navigateToApp(storedRole);
        } else {
            if (storedRole === 'null') {
                setLoginResponse({ message: "Your role is not valid. Please try to login or contact your administrator!", hasError: true });
            }
        }
    }, [navigate]);

    const postLogin = async (email, firstName, lastName) => {
        try {
            setLoading(true);
            const response = await axios.post(POST_LOGIN, {
                Email: email,
                FirstName: firstName,
                LastName: lastName,
            });

            if (["Family", "MLTUser"].includes(response.data.role) && response.data.assignees.length > 0) {
                localStorage.setItem('assignees', JSON.stringify(response.data.assignees));
            }

            // Store role and username in localStorage
            localStorage.setItem('userId', response.data.userId);
            localStorage.setItem('email', email);
            localStorage.setItem('firstName', firstName);
            localStorage.setItem('lastName', lastName);
            localStorage.setItem('username', response.data.userName);
            localStorage.setItem('role', response.data.role);
            localStorage.setItem('rootRole', response.data.rootRole);
            localStorage.setItem('filterString', response.data.filterString);

            const queryParams = new URLSearchParams(location.search);
            const returnUrl = queryParams.get('returnUrl');

            // Load cached cold data
            var tMarsCache = {};
            var [
                EventTypes,
                EventFormats,
                EventLastVisit,
                EventAssociates,
                EventStatus,
                SiteTypes,
                Countries,
                SiteCodes,
                Regions,
                CountryCodes,
                UserTypes,
                SiteDetails,
            ] = await Promise.all([
                axios.get(EVENT_TYPE_URL),
                axios.get(EVENT_FORMAT_URL),
                axios.get(EVENT_LAST_VISIT_URL),
                axios.get(EVENT_ASSOCIATE_URL),
                axios.get(EVENT_STATUS_URL),
                axios.get(GET_SITETYPES),
                axios.get(COUNTRY_URL),
                axios.get(GET_SITECODES),
                axios.get(GET_REGIONS),
                axios.get(COUNTRY_CODE_URL),
                axios.get(USER_TYPES_URL),
                axios.get(SITE_DETAILS_URL),
            ]);
            tMarsCache.EventTypes = EventTypes.data;
            tMarsCache.EventFormats = EventFormats.data;
            tMarsCache.EventLastVisit = EventLastVisit.data;
            tMarsCache.EventAssociates = EventAssociates.data;
            tMarsCache.EventStatus = EventStatus.data;
            tMarsCache.SiteTypes = SiteTypes.data;
            tMarsCache.Countries = Countries.data;
            tMarsCache.SiteCodes = SiteCodes.data;
            tMarsCache.SiteDetails = SiteDetails.data.map((site) => ({
                                                        name: (site.site_code + " - " + site.unit) || "Unknown",
                                                        lat: site.latitude ? parseFloat(site.latitude) : null,
                                                        lng: site.longitude ? parseFloat(site.longitude) : null, }))
//                                                     .filter((site) => site.lat !== null && site.lng !== null)
                                                     .sort((a, b) => a.name.localeCompare(b.name));
            tMarsCache.Regions = Regions.data;
            tMarsCache.CountryCodes = CountryCodes.data;
            tMarsCache.UserTypes = UserTypes.data;
//            window.MarsCache = tMarsCache;
//            console.log("AKO window.MarsCache: "+JSON.stringify(window.MarsCache));
            localStorage.setItem('MarsCache',JSON.stringify(tMarsCache));

            setShowPopup(response.data.welcomePopUpIsActive);
            setAppToShow(response.data.role);
            setLoginInProgress(false);
            
            setLoading(false);
            setLoginResponse({ message: "Login successful!", hasError: false });

            (returnUrl !== null) && (returnUrl !== "/login") ? navigate(returnUrl) : navigateToApp(response.data.role);
        } catch (error) {
            setLoading(false);
            setLoginInProgress(false);
            if (error.response) {
                setLoginResponse({ message: error.response.data.message || 'Error during login process: '+JSON.stringify(error.response), hasError: true });
                console.error('Error response:', JSON.stringify(error.response.data));
            } else if (error.request) {
                console.error('Error request:', error.request);
                setLoginResponse({ message: 'No response from server', hasError: true });
            } else {
                console.error('Error message:', error.message);
                setLoginResponse({ message: 'Error in request setup: '+
                                            + ((error.name || error.message) ? ":" 
                                                + (error.name ? " "+error.name : "") 
                                                + (error.message ? " "+error.message : "") 
                                                + (error.errorCode ? " "+error.errorCode : "") 
                                                + (error.errorMessage ? " "+error.errorMessage : "") 
                                                + (error.subError ? " "+error.subError : "") 
                                                : "")
                                            , hasError: true });
            }
        }
    }

const clearAllCookies = () => {
   document.cookie.split(";").forEach(cookie => {
       const eqPos = cookie.indexOf("=");
       const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
       document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
   });
}

const handleLogin = async (event) => {
        if (loginInProgress) {
            alert("Login is already in progress.");
            return;
        }
        event.preventDefault();
        setLoginInProgress(true);
        clearAllCookies();
        localStorage.clear();
        sessionStorage.clear();
/*
        localStorage.removeItem('role');
        localStorage.removeItem('hidePopup');
        localStorage.removeItem('assignees');
        localStorage.removeItem('userId');
        localStorage.removeItem('email');
        localStorage.removeItem('firstName');
        localStorage.removeItem('lastName');
        localStorage.removeItem('username');
        localStorage.removeItem('role');
        localStorage.removeItem('rootRole');
        localStorage.removeItem('filterString');
        localStorage.removeItem('MarsCache');
        localStorage.removeItem('reportsFilter');
        localStorage.removeItem('impersonatedUser');
        localStorage.removeItem('fromAdminMode');
        localStorage.removeItem('showdialogue');
*/        

        try {
            const loginResponse = await instance.loginPopup(loginRequest);
            if (loginResponse.account) {
              try {
                const claims = loginResponse.idTokenClaims || {};
                const email = claims.email ?? claims.emails?.[0] ?? "";
                const lastName = claims.family_name ?? "";
                const firstName = claims.given_name ?? "";

                await postLogin(email, firstName, lastName);
              } catch (error) {
                 setLoginInProgress(false);
                console.error("Technical error during login", {
                    errorCode: error.errorCode,
                    errorMessage: error.errorMessage,
                    subError: error.subError,
                    name: error.name,
                    message: error.message
                });
                 setLoginResponse({ message: "Technical error during login"
                                                + ((error.name || error.message) ? ":" 
                                                 + (error.name ? " "+error.name : "") 
                                                 + (error.message ? " "+error.message : "") 
                                                 + (error.errorCode ? " "+error.errorCode : "") 
                                                 + (error.errorMessage ? " "+error.errorMessage : "") 
                                                 + (error.subError ? " "+error.subError : "") 
                                                 : "")
                                             , hasError: true });
              }
            }
        } catch (error) {
            setLoginInProgress(false);
            if (error.errorMessage && error.errorMessage.includes("AADB2C90118")) {
                await resetPasswordAsync();
                return;
            }
            console.error("Login failed:", {
                errorCode: error.errorCode,
                errorMessage: error.errorMessage,
                subError: error.subError,
                name: error.name,
                message: error.message
            });
            setLoginResponse({ message: 'Login failed'
                                        + ((error.name || error.message) ? ":" 
                                         + (error.name ? " "+error.name : "") 
                                         + (error.message ? " "+error.message : "") 
                                         + (error.errorCode ? " "+error.errorCode : "") 
                                         + (error.errorMessage ? " "+error.errorMessage : "") 
                                         + (error.subError ? " "+error.subError : "") 
                                         : "")
                                    , hasError: true });
        }
    };

    const resetPasswordAsync = async () => {
        try {
            const resetResponse = await instance.loginPopup(resetPassword);
            if (resetResponse.account) {
                const email = resetResponse.idTokenClaims.email;
                const lastName = resetResponse.idTokenClaims.family_name;
                const firstName = resetResponse.idTokenClaims.given_name;

                postLogin(email, firstName, lastName);
            }
        } catch (resetError) {
            console.error("Password reset failed:", resetError);
            setLoginResponse({ message: 'Password reset failed', hasError: true });
        }
    }

    const handlePopupClose = (role) => {
        navigateToApp(role);
        // Hide the popup after redirection
        setShowPopup(false);
    };

    return (
        <div className="login-container" style={{ position: 'relative' }}>
            {/* Conditionally show the corresponding app in the background */}
            {showApp === 'Requester' && (
                <div style={{ position: 'absolute', zIndex: 0, top: 0, left: 0, right: 0, bottom: 0 }}>
                    <RequesterApp />
                </div>
            )}
            {showApp === 'Admin' && (
                <div style={{ position: 'absolute', zIndex: 0, top: 0, left: 0, right: 0, bottom: 0 }}>
                    <MarsAdmin />
                </div>
            )}
            {showApp === 'Family' && (
                <div style={{ position: 'absolute', zIndex: 0, top: 0, left: 0, right: 0, bottom: 0 }}>
                    <MarsFamilyComplete
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 0,  // Ensure MarsFamilyComplete is behind the popup
                        }}
                        mapProps={{
                            style: {  // Ensure the map is full-screen in the background
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 0,
                            },
                        }}
                        leftContainerEvent={{
                            style: {  // Ensure leftContainerEvent is correctly placed
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                zIndex: 0,
                            },
                        }}
                    />
                </div>
            )}

            {!showApp && (
                <div className="login-form">
                    <div className="logo-container">
                        <img src={Logo} alt="Logo" className="logo" />
                    </div>
                    <div className="row" style={{ marginBottom: '1rem' }}>
                        <div className="col-6 login-text">
                            <h4>Touch App</h4>
                        </div>
                        <div className="col-6 login-icone">
                            <i className="fa-solid fa-users-rays"></i>
                        </div>
                    </div>
                    {!loading ? (
                        <button onClick={handleLogin} className="loginBtn" disabled={loginInProgress}>Login</button>
                    ) : (
                        <div>Connecting...</div>
                    )}

                    {loginResponse.message && !loading && (
                        <p className={loginResponse.hasError ? 'error-message' : 'success-message'}>
                            {loginResponse.message}
                        </p>
                    )}
                </div>
            )}

            {/* Show the InformationPopup after successful login, with a higher z-index */}
            {showPopup && (
                <div style={{ position: 'absolute', zIndex: 2 }}>
                    <InformationPopup isOpen={showPopup} role={showApp} onClose={handlePopupClose} />
                </div>
            )}
        </div>
    );
};

export default Login;
